import React, { useState, useEffect } from 'react';
import emailApi from '../services/emailApi';

interface CalendarEmail {
  id: number;
  subject: string;
  status: string;
  time: string;
}

interface CalendarDay {
  date: string;
  pending: number;
  sent: number;
  failed: number;
  emails: CalendarEmail[];
}

const CalendarView: React.FC = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [days, setDays] = useState<Record<string, CalendarDay>>({});
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await emailApi.getCalendarData(year, month);
        setDays((data as Record<string, unknown>).data as Record<string, CalendarDay> || {});
      } catch {
        // silently fail
      }
    };
    load();
  }, [year, month]);

  const monthNames = ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
  const dayNames = ['Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sam', 'Dum'];

  const getDaysInMonth = (y: number, m: number) => new Date(y, m, 0).getDate();
  const getFirstDayOfWeek = (y: number, m: number) => { const d = new Date(y, m - 1, 1).getDay(); return d === 0 ? 6 : d - 1; };

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };
  const goToday = () => { setYear(new Date().getFullYear()); setMonth(new Date().getMonth() + 1); };

  const totalDays = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const today = new Date().toISOString().split('T')[0];

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(<div key={`empty-${i}`} style={{ padding: '8px', minHeight: '80px' }} />);
  for (let d = 1; d <= totalDays; d++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dayData = days[dateStr];
    const isToday = dateStr === today;
    cells.push(
      <div key={dateStr} className="calendar-cell" onClick={() => dayData && setSelectedDay(dayData)} style={{
        padding: '6px', minHeight: '80px', border: isToday ? '2px solid #3498db' : '1px solid var(--border-color, #334155)',
        borderRadius: '8px', cursor: dayData ? 'pointer' : 'default',
        backgroundColor: isToday ? 'var(--highlight-bg, #1e3a5f)' : 'var(--card-bg, #1e293b)', transition: 'background 0.2s'
      }}>
        <div style={{ fontWeight: isToday ? 'bold' : 'normal', fontSize: '13px', marginBottom: '4px', color: 'var(--text-primary, #e2e8f0)' }}>{d}</div>
        {dayData && (
          <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
            {dayData.pending > 0 && <span style={{ fontSize: '10px', backgroundColor: '#f39c12', color: 'white', borderRadius: '4px', padding: '1px 5px' }}>{dayData.pending}</span>}
            {dayData.sent > 0 && <span style={{ fontSize: '10px', backgroundColor: '#27ae60', color: 'white', borderRadius: '4px', padding: '1px 5px' }}>{dayData.sent}</span>}
            {dayData.failed > 0 && <span style={{ fontSize: '10px', backgroundColor: '#e74c3c', color: 'white', borderRadius: '4px', padding: '1px 5px' }}>{dayData.failed}</span>}
          </div>
        )}
      </div>
    );
  }

  const statusColor = (s: string) => s === 'sent' ? '#27ae60' : s === 'failed' ? '#e74c3c' : '#f39c12';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <button onClick={prevMonth} style={{ padding: '6px 14px', border: '1px solid var(--border-color, #334155)', borderRadius: '6px', cursor: 'pointer', background: 'var(--card-bg, #1e293b)', color: 'var(--text-primary, #e2e8f0)' }}>&#9664;</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '20px' }}>{monthNames[month - 1]} {year}</h2>
          <button onClick={goToday} style={{ padding: '4px 10px', border: '1px solid #3498db', borderRadius: '6px', cursor: 'pointer', background: 'var(--card-bg, #1e293b)', color: '#3498db', fontSize: '12px' }}>Azi</button>
        </div>
        <button onClick={nextMonth} style={{ padding: '6px 14px', border: '1px solid var(--border-color, #334155)', borderRadius: '6px', cursor: 'pointer', background: 'var(--card-bg, #1e293b)', color: 'var(--text-primary, #e2e8f0)' }}>&#9654;</button>
      </div>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', fontSize: '12px' }}>
        <span>{'🟡'} Programat</span><span>{'🟢'} Trimis</span><span>{'🔴'} Esuat</span>
      </div>
      <div className="calendar-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
        {dayNames.map(d => <div key={d} className="calendar-day-name" style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '12px', padding: '8px', color: 'var(--text-secondary, #94a3b8)' }}>{d}</div>)}
        {cells}
      </div>
      {selectedDay && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}
          onClick={(e) => { if (e.target === e.currentTarget) setSelectedDay(null); }}>
          <div style={{ backgroundColor: 'var(--card-bg, #1e293b)', borderRadius: '12px', padding: '24px', maxWidth: '500px', width: '90%', maxHeight: '70vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary, #e2e8f0)' }}>{selectedDay.date}</h3>
              <button onClick={() => setSelectedDay(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-primary, #e2e8f0)' }}>{'\u2715'}</button>
            </div>
            {selectedDay.emails.map(e => (
              <div key={e.id} style={{ padding: '10px', borderBottom: '1px solid var(--border-color, #334155)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: '14px', color: 'var(--text-primary, #e2e8f0)' }}>{e.subject}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary, #94a3b8)' }}>{e.time}</div>
                </div>
                <span style={{ fontSize: '11px', backgroundColor: statusColor(e.status), color: 'white', borderRadius: '4px', padding: '2px 8px' }}>{e.status}</span>
              </div>
            ))}
            {selectedDay.emails.length === 0 && <p style={{ color: 'var(--text-secondary, #94a3b8)', textAlign: 'center' }}>Niciun email in aceasta zi</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
